import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { RefreshCw, Search, ChevronRight, MessageCircle, PlusCircle, Plane, ShoppingBag } from "lucide-react";
import { Conversation } from "@/types/conversation";
import { format } from "date-fns";
import { fetchUserRefundRequests } from "@/utils/api/refund-requests";

const Conversations = () => {
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchConversations = async () => {
      setLoading(true);
      try {
        console.log("🔍 Fetching all user conversations...");
        const data = await fetchUserRefundRequests();
        console.log("✅ Fetched conversations:", data.map(c => ({ 
          id: c.id, 
          type: c.request_type, 
          merchant: c.merchant,
          requestType: c.request_type 
        })));
        console.log("🔍 Raw conversation data:", data);
        setConversations(data);
      } catch (error) {
        console.error("Error fetching conversations:", error);
      } finally {
        setTimeout(() => {
          setLoading(false);
        }, 600); // Short delay for better UX
      }
    };

    fetchConversations();
  }, []);

  const filteredConversations = conversations.filter(conversation => 
    conversation.merchant?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conversation.product?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conversation.issue?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleConversationClick = (id: string) => {
    navigate(`/conversation/${id}`);
  };

  return (
    <div className="container py-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold">Your Conversations</h1>
          <p className="text-muted-foreground">View and manage all your refund requests</p>
        </div>
        
        <div className="w-full md:w-auto flex gap-2">
          <div className="relative w-full md:w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search conversations..."
              className="pl-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={loading}
            />
          </div>
          <Button asChild>
            <Link to="/refund" className="flex items-center gap-1.5">
              <PlusCircle className="h-4 w-4" />
              New Request
            </Link>
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="grid gap-4 animate-pulse">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="cursor-pointer hover:border-primary/50 transition-colors">
              <CardHeader className="pb-2">
                <div className="flex justify-between">
                  <Skeleton className="h-6 w-48" />
                  <Skeleton className="h-6 w-24" />
                </div>
              </CardHeader>
              <CardContent>
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2 mb-2" />
                <div className="flex justify-between mt-4">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-24" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredConversations.length > 0 ? (
        <div className="grid gap-4 animate-fade-in">
          {filteredConversations.map((conversation) => (
            <Card 
              key={conversation.id} 
              className="cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => handleConversationClick(conversation.id)}
            >
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    {(conversation.request_type === "flight_compensation" || 
                      conversation.issue?.toLowerCase().includes("flight") ||
                      conversation.issue?.toLowerCase().includes("baggage") ||
                      conversation.product?.toLowerCase().includes("flight")) ? (
                      <Plane className="h-4 w-4 text-blue-600" />
                    ) : (
                      <ShoppingBag className="h-4 w-4 text-green-600" />
                    )}
                    <CardTitle className="text-lg font-medium">
                      {conversation.merchant || "Unknown Merchant"}
                    </CardTitle>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold text-muted-foreground bg-muted/50">
                      {(conversation.request_type === "flight_compensation" || 
                        conversation.issue?.toLowerCase().includes("flight") ||
                        conversation.issue?.toLowerCase().includes("baggage") ||
                        conversation.product?.toLowerCase().includes("flight")) ? "Flight Claim" : "Refund Request"}
                    </span>
                    <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold bg-yellow-100 text-yellow-800">
                      {conversation.status || "In Progress"}
                    </span>
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                 <p className="text-sm mb-1"><span className="font-medium">Product:</span> {conversation.product || "Not specified"}</p>
                <p className="text-sm text-muted-foreground line-clamp-2">{conversation.issue || "No description provided"}</p>
                <div className="flex justify-between mt-4 text-xs text-muted-foreground">
                  <span>
                    {conversation.currency && conversation.currency !== 'USD' 
                      ? `${conversation.currency} ${conversation.amount?.toFixed(2) || "0.00"}`
                      : `$${conversation.amount?.toFixed(2) || "0.00"}`}
                  </span>
                  <span className="flex items-center">
                    <MessageCircle className="h-3.5 w-3.5 mr-1.5" />
                    {conversation.created_at 
                      ? format(new Date(conversation.created_at), "MMM d, yyyy") 
                      : "Unknown date"}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 animate-fade-in">
          <MessageCircle className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">No conversations found</h2>
          <p className="text-muted-foreground mb-6">
            {searchQuery ? "No conversations match your search criteria." : "You haven't created any refund requests yet."}
          </p>
          <Button asChild>
            <Link to="/refund" className="flex items-center gap-1.5">
              <PlusCircle className="h-4 w-4" />
              Create Your First Request
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
};

export default Conversations;
